import { RefObject, useEffect } from 'react'

const FOCUSABLE_SELECTOR =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), iframe, [tabindex]:not([tabindex="-1"])'

/**
 * Moves the focus into the container when it mounts, keeps Tab and Shift+Tab inside it,
 * and gives the focus back to the previously focused element when it unmounts.
 * Elements inside an `aria-hidden` region are skipped.
 */
const useFocusTrap = (containerRef: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const previouslyFocusedElement = document.activeElement as HTMLElement | null
    container.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const focusableElements = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => !element.closest('[aria-hidden="true"]'))
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      if (!firstElement || !lastElement) {
        event.preventDefault()
        return
      }
      const isFocusOnContainerOrFirst =
        document.activeElement === container || document.activeElement === firstElement
      if (event.shiftKey && isFocusOnContainerOrFirst) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => {
      container.removeEventListener('keydown', handleKeyDown)
      previouslyFocusedElement?.focus()
    }
  }, [containerRef])
}

export { useFocusTrap }
